import { TimezoneId } from "@nova/constants"
import { createAsyncThunk, createSelector, createSlice } from "@reduxjs/toolkit"
import { fetchApiGateway } from "@nova/fetch-api-gateway"
import { RootState } from "integrations/redux"

export const fetchProfile = createAsyncThunk("profile-settings/fetchProfile", async () => {
    return await fetchApiGateway("get /me/profile")
})

type ProfileSlice = {
    firstName: string
    lastName: string
    email: string
    phone: string
    organizationName: string
    title: string
    defaultWorkspaceId: string
    timezoneId: TimezoneId | null
    isFetchingProfile: boolean
    hasFetchedProfile: boolean
    hasUnsavedChanges: boolean
    isSavingChanges: boolean
}

const initialState: ProfileSlice = {
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    organizationName: "",
    title: "",
    defaultWorkspaceId: "",
    timezoneId: null,
    isFetchingProfile: false,
    hasFetchedProfile: false,
    hasUnsavedChanges: false,
    isSavingChanges: false,
}

export const profileSliceName = "profile" as const

export const {
    actions: {
        setFirstName,
        setLastName,
        setPhone,
        setOrganizationName,
        setTitle,
        setDefaultWorkspaceId,
        setTimezoneId,
        setIsSavingChanges,
        clearLocalChanges,
    },
    reducer: ProfileSliceReducer,
} = createSlice({
    name: profileSliceName,
    initialState,
    reducers: {
        setFirstName: (state, action: { payload: ProfileSlice["firstName"] }) => {
            state.firstName = action.payload
            state.hasUnsavedChanges = true
        },
        setLastName: (state, action: { payload: ProfileSlice["lastName"] }) => {
            state.lastName = action.payload
            state.hasUnsavedChanges = true
        },
        setPhone: (state, action: { payload: ProfileSlice["phone"] }) => {
            state.phone = action.payload
            state.hasUnsavedChanges = true
        },
        setOrganizationName: (state, action: { payload: ProfileSlice["organizationName"] }) => {
            state.organizationName = action.payload
            state.hasUnsavedChanges = true
        },
        setTitle: (state, action: { payload: ProfileSlice["title"] }) => {
            state.title = action.payload
            state.hasUnsavedChanges = true
        },
        setDefaultWorkspaceId: (state, action: { payload: ProfileSlice["defaultWorkspaceId"] }) => {
            state.defaultWorkspaceId = action.payload
            state.hasUnsavedChanges = true
        },
        setTimezoneId: (state, action: { payload: ProfileSlice["timezoneId"] }) => {
            state.timezoneId = action.payload
            state.hasUnsavedChanges = true
        },
        setIsSavingChanges: (state, action: { payload: ProfileSlice["isSavingChanges"] }) => {
            state.isSavingChanges = action.payload
        },
        clearLocalChanges: (state) => {
            state.hasUnsavedChanges = false
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchProfile.pending, (state) => {
                state.isFetchingProfile = true
            })
            .addCase(fetchProfile.fulfilled, (_, action) => {
                const res = action.payload
                return {
                    ...initialState,
                    firstName: res.givenName || "",
                    lastName: res.familyName || "",
                    email: res.email || "",
                    phone: res.phoneNumber || "",
                    organizationName: res.organizationName || "",
                    title: res.title || "",
                    defaultWorkspaceId: res.defaultWorkspaceId || "",
                    timezoneId: res.timezoneId || null,
                    hasFetchedProfile: true,
                    isFetchingProfile: false,
                }
            })
            .addCase(fetchProfile.rejected, (state) => {
                state.isFetchingProfile = false
            })
    },
})

const root = (state: RootState) => state[profileSliceName]

export const selectFirstName = createSelector([root], (s) => s.firstName)
export const selectLastName = createSelector([root], (s) => s.lastName)
export const selectEmail = createSelector([root], (s) => s.email)
export const selectPhone = createSelector([root], (s) => s.phone)
export const selectOrganizationName = createSelector([root], (s) => s.organizationName)
export const selectTitle = createSelector([root], (s) => s.title)
export const selectDefaultWorkspaceId = createSelector([root], (s) => s.defaultWorkspaceId)
export const selectTimezoneId = createSelector([root], (s) => s.timezoneId)

export const selectIsFetchingProfile = createSelector([root], (s) => s.isFetchingProfile)
export const selectHasFetchedProfile = createSelector([root], (s) => s.hasFetchedProfile)
export const selectHasUnsavedChanges = createSelector([root], (s) => s.hasUnsavedChanges)
export const selectIsSavingChanges = createSelector([root], (s) => s.isSavingChanges)
